import { Form, FormInstance, Input, InputNumber, Modal, Select } from "antd";
import React, { useEffect, useState } from "react";
import { Kind, Order, OrderType } from "../../types/order.type";
import useGetRequest from "../../hooks/useGetRequest";
import { Material } from "../../types/material.type";
import { Produce } from "../../types/produce.type";
import { Currency } from "../../types/currency.type";
import { Machine } from "../../types/machine.type";
import { lom, sim } from "../../config/config";
import { useExchangeRate } from "../../contexts/exchangeRate.context";
import { crmApi } from "../../api/crmApi";
import { User, UserRole } from "../../types/user.type";

const { Option } = Select;
const { TextArea } = Input;

interface CreateOrderProps {
  title: string;
  open: boolean;
  form: FormInstance;
  confirmLoading: boolean;
  onCancel: () => void;
  onOk: () => void;
  onFinish: (values: any) => void;
  clientType: string;
  initialValues?: Record<string, any>;
  disabledFields?: string[];
  totalMax?: number;
}

const CreateOrder: React.FC<CreateOrderProps> = ({
  title,
  open,
  form,
  confirmLoading,
  onCancel,
  onOk,
  onFinish,
  clientType,
  initialValues,
  disabledFields = [],
  totalMax,
}) => {
  const defaultKindId = clientType === "Лом" ? lom : null;
  const defaultKind = clientType === "Лом" ? Kind.MATERIAL : null;
  const defaultType =
    clientType === "Продажа" ? OrderType.SALE : OrderType.PURCHASE;
  const [selectedKind, setSelectedKind] = useState<Kind | null>(defaultKind);
  const [totalCost, setTotalCost] = useState({
    USD: 0,
    TJS: 0,
  });
  const [discount, setDiscount] = useState(0);
  const { exchangeRate } = useExchangeRate();

  const { data: producesList, loading: produceLoading } =
    useGetRequest("/produce/list");
  const { data: materialsList, loading: materialLoading } =
    useGetRequest("/material/list");
  const { data: machinesList, loading: machineLoading } =
    useGetRequest("/machine/list");
  const [salesUsers, setSalesUsers] = useState<User[]>([]);
  const [salesUsersLoading, setSalesUsersLoading] = useState(false);
  const isDisabled = (field: string) => disabledFields.includes(field);
  const watchedType = Form.useWatch("type", form) as OrderType | undefined;
  const effectiveType = watchedType || defaultType;
  const watchedKind = Form.useWatch("kind", form) as Kind | undefined;
  const watchedKindId = Form.useWatch("kindId", form) as string | undefined;
  
  // Получаем единицу измерения второго остатка из выбранной продукции
  const selectedProduce = watchedKindId && watchedKind === Kind.PRODUCE && producesList
    ? (producesList as Produce[]).find((p: Produce) => p._id === watchedKindId)
    : null;
  const residueKgUnit = selectedProduce?.residueKgUnit || "кг";

  const handleKindChange = (value: Kind) => {
    setSelectedKind(value);
    form.setFieldsValue({ kindId: null });
  };

  // Когда выставляем initialValues через form.setFieldsValue — onChange у Select не срабатывает,
  // поэтому selectedKind может остаться null и список "Элемент" будет пустой.
  useEffect(() => {
    if (watchedKind) {
      setSelectedKind(watchedKind);
    }
  }, [watchedKind]);

  const calculateTotalCost = (values: any) => {
    const { currency, total, price, discount } = values;

    if (
      !currency ||
      total === undefined ||
      price === undefined ||
      !exchangeRate
    )
      return;

    let costInUSD = total * price;

    if (discount && effectiveType === OrderType.SALE) {
      costInUSD = costInUSD - (costInUSD * discount) / 100;
    }

    if (currency === Currency.US_DOLLAR) {
      setTotalCost({
        USD: costInUSD,
        TJS: costInUSD * exchangeRate,
      });
    } else {
      setTotalCost({
        USD: costInUSD / exchangeRate,
        TJS: costInUSD,
      });
    }
  };

  const getKindOptions = () => {
    // Используем watchedKind (актуальное значение формы) вместо selectedKind (state с задержкой)
    // Также проверяем initialValues.kind для случая когда форма еще не синхронизирована
    const effectiveKind = watchedKind || selectedKind || initialValues?.kind;
    
    switch (effectiveKind) {
      case Kind.PRODUCE:
        return producesList?.map((produce: Produce) => (
          <Option key={produce._id} value={produce._id}>
            {produce.name} ({produce.unitOfMeasurement})
          </Option>
        ));
      case Kind.MATERIAL:
        return materialsList
          ?.filter((material: Material) => {
            if (clientType === "Лом") {
              return material._id === lom || material._id === sim;
            }
            return true;
          })
          .map((material: Material) => (
            <Option key={material._id} value={material._id}>
              {material.name} ({material.unitOfMeasurement})
            </Option>
          ));
      default:
        return null;
    }
  };

  useEffect(() => {
    if (exchangeRate) {
      form.setFieldsValue({ exchangeRate });
    }
  }, [exchangeRate, form]);

  useEffect(() => {
    if (!open) return;
    if (!initialValues) return;

    // Сначала устанавливаем selectedKind, чтобы getKindOptions() не вернул null
    if (initialValues.kind) {
      setSelectedKind(initialValues.kind);
    }
    
    // Устанавливаем все значения формы, включая kindId
    form.setFieldsValue(initialValues);
    
    if (typeof initialValues.discount === "number") {
      setDiscount(initialValues.discount);
    }
    calculateTotalCost({ ...form.getFieldsValue(), ...initialValues });
  }, [open, initialValues, form]);

  // Дополнительный useEffect: после загрузки списков повторно проставим kindId
  useEffect(() => {
    if (!open) return;
    if (!initialValues?.kindId) return;
    if (produceLoading || materialLoading) return;
    
    // Списки загружены, убедимся что kindId проставлен
    const currentKindId = form.getFieldValue("kindId");
    if (!currentKindId && initialValues.kindId) {
      form.setFieldsValue({ kindId: initialValues.kindId });
    }
  }, [open, produceLoading, materialLoading, initialValues, form]);

  // Когда списки produces/materials приходят позже — Select может не отобразить label для kindId.
  // Форсим повторное проставление kindId после загрузки options.
  useEffect(() => {
    if (!open) return;
    const kindId = initialValues?.kindId;
    if (!kindId) return;
    
    // Проверяем, что текущее значение в форме отличается от того, что должно быть
    const currentKindId = form.getFieldValue("kindId");
    if (currentKindId !== kindId) {
      form.setFieldsValue({ kindId });
    }
  }, [open, initialValues?.kindId, producesList, materialsList, form]);

  useEffect(() => {
    // Загружаем список продажников только для заказов продажи
    if (effectiveType === OrderType.SALE) {
      setSalesUsersLoading(true);
      crmApi
        .getSalesUsers()
        .then((users) => setSalesUsers(users))
        .catch((err) => {
          console.error("Ошибка при загрузке продажников", err);
        })
        .finally(() => setSalesUsersLoading(false));
    }
  }, [effectiveType]);

  const handleFinish = (values: any) => {
    onFinish({
      ...values,
      exchangeRate: exchangeRate || values.exchangeRate,
    });
  };

  return (
    <Modal
      title={title}
      open={open}
      onCancel={onCancel}
      onOk={onOk}
      confirmLoading={confirmLoading}
    >
      <Form
        form={form}
        onFinish={handleFinish}
        layout="vertical"
        onValuesChange={(_, allValues) => calculateTotalCost(allValues)}
      >
        <Form.Item
          name="type"
          label="Тип заказа"
          rules={[{ required: true, message: "Выберите тип заказа" }]}
          initialValue={defaultType}
        >
          <Select
            placeholder="Выберите тип заказа"
            disabled={true}
          >
            {Object.values(OrderType).map((type) => (
              <Option key={type} value={type}>
                {type}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="kind"
          label="Тип сущ."
          rules={[{ required: true, message: "Выберите тип сущ." }]}
          initialValue={defaultKind}
        >
          <Select
            placeholder="Выберите тип сущ."
            onChange={handleKindChange}
            disabled={isDisabled("kind") || defaultKind !== null}
          >
            {Object.values(Kind).map((kind) => (
              <Option key={kind} value={kind}>
                {kind}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="kindId"
          label="Элемент"
          rules={[{ required: true, message: "Выберите элемент" }]}
          initialValue={clientType === "Лом" ? lom : defaultKindId}
        >
          <Select
            placeholder="Выберите элемент"
            loading={produceLoading || materialLoading}
            disabled={isDisabled("kindId")}
            showSearch
            filterOption={(input, option) => {
              const optionText = option?.label || option?.children;
              return String(optionText).toLowerCase().includes(input.toLowerCase());
            }}
            optionFilterProp="children"
          >
            {getKindOptions()}
          </Select>
        </Form.Item>

        {(watchedKind === Kind.PRODUCE || selectedKind === Kind.PRODUCE) && effectiveType === OrderType.SALE && (
          <Form.Item
            name="machineId"
            label="Станок"
            rules={[{ required: true, message: "Выберите станок" }]}
            initialValue={defaultKind}
          >
            <Select
              placeholder="Выберите станок"
              showSearch
              filterOption={(input, option) => {
                const optionText = option?.label || option?.children;
                return String(optionText).toLowerCase().includes(input.toLowerCase());
              }}
              optionFilterProp="children"
            >
              {machinesList.map((machine: Machine) => (
                <Option key={machine._id} value={machine._id}>
                  {machine.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        )}

        <Form.Item
          name="currency"
          label="Валюта"
          rules={[{ required: true, message: "Выберите валюту" }]}
        >
          <Select placeholder="Выберите валюту" disabled={isDisabled("currency")}>
            {Object.values(Currency).map((currency) => (
              <Option key={currency} value={currency}>
                {currency}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="total"
          label="Количество"
          rules={[{ required: true, message: "Введите количество" }]}
        >
          <InputNumber style={{ width: 150 }} min={0} max={totalMax} />
        </Form.Item>

        {(watchedKind === Kind.PRODUCE || selectedKind === Kind.PRODUCE) && (
          <Form.Item
            name="totalKg"
            label={`Количество (2) ${watchedKindId ? `(${residueKgUnit})` : ""}`}
            rules={[{ required: true, message: `Введите количество (2) ${watchedKindId ? `в ${residueKgUnit}` : ""}` }]}
          >
            <InputNumber 
              style={{ width: 150 }} 
              addonAfter={watchedKindId ? residueKgUnit : undefined}
              placeholder={watchedKindId ? `Введите количество в ${residueKgUnit}` : "Введите количество (2)"}
            />
          </Form.Item>
        )}

        <Form.Item
          name="price"
          label="Цена"
          rules={[{ required: true, message: "Введите цену" }]}
        >
          <InputNumber style={{ width: 150 }} disabled={isDisabled("price")} />
        </Form.Item>

        <Form.Item
          name="exchangeRate"
          label="Курс"
          hidden={true}
        >
          <InputNumber style={{ width: 150 }} />
        </Form.Item>

        {effectiveType === OrderType.SALE && (
          <Form.Item
            name="discount"
            label="Скидка (%)"
            rules={[{ required: false, message: "Введите скидку" }]}
          >
            <InputNumber
              min={0}
              max={100}
              style={{ width: 150 }}
              onChange={(value) => setDiscount(value || 0)}
              disabled={isDisabled("discount")}
            />
          </Form.Item>
        )}

        {/* Связь с заказом-нарядом (для продажи по частям) */}
        <Form.Item name="taskOrderId" hidden={true}>
          <Input />
        </Form.Item>

        {effectiveType === OrderType.SALE && (
          <Form.Item
            name="salesUserId"
            label="Продажник"
          >
            <Select
              placeholder="Выберите продажника (опционально)"
              loading={salesUsersLoading}
              allowClear
            >
              {salesUsers
                .filter((u) => u.role === UserRole.SalesDept)
                .map((user: User) => (
                  <Option key={user._id} value={user._id}>
                    {user.name} ({user.username})
                  </Option>
                ))}
            </Select>
          </Form.Item>
        )}

        <Form.Item
          name="description"
          label="Описание"
        >
          <TextArea
            rows={3}
            placeholder="Дополнительная информация о заказе (опционально)"
            maxLength={500}
            showCount
          />
        </Form.Item>

        <div style={{ marginTop: 16, fontWeight: "bold" }}>
          Общая стоимость:
          <br />
          USD: {totalCost.USD.toFixed(2)}
          <br />
          TJS: {totalCost.TJS.toFixed(2)}
        </div>
      </Form>
    </Modal>
  );
};

export default CreateOrder;
